/* eslint-disable react/forbid-prop-types,import/prefer-default-export,jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useQuery } from 'react-query';
import { isMobile } from 'react-device-detect';
import { exchange } from '../../../../../utilities/constants';
import { formatNumber, formatNumberWithComma } from '../../../../../utilities/formatHelpers';
import { USER_PREFERENCES } from '../../../../../utilities/sharedQueries';
import BasicContainer from '../../../../../components/BasicContainer';
import { PercentageBadge } from '../../../../../components/shared';
import { useAccountRestriction } from '../../../../../utilities/sharedHooks/account';
import { BalanceCardPlaceholder } from '../../../../../components/Placeholders';
import { actionTypes, MODAL_SCREENS } from '../../../../../screens/Accounts/constants';

export const Balance = ({
  totalBalanceInFiat,
  fiatCurrencyCode = '',
  onCtaClick = null,
  totalChangeInFiat = null,
  wallets = null,
  loading = false,
  showModal,
  isDepositHidden,
}) => {
  const { t: translate } = useTranslation();
  const { data: userPref } = useQuery(USER_PREFERENCES);
  const userFiatCurrencyCode = fiatCurrencyCode || userPref?.displayCurrency || 'EUR';
  const [showPopover, setShowPopover] = useState(false);

  const totalBalanceFiatLabel = `${formatNumberWithComma(
    formatNumber(totalBalanceInFiat, exchange.FIAT_VALUE)
  )} ${userFiatCurrencyCode}`;

  const changesPercentage = totalChangeInFiat / totalBalanceInFiat;

  const labelSign = useMemo(() => {
    let sign;
    if (totalChangeInFiat / 100.0 > 0) {
      sign = '+';
    } else if (totalChangeInFiat / 100.0 < 0) {
      sign = '-';
    } else {
      sign = '';
    }
    return sign;
  }, [totalChangeInFiat]);

  const totalChangeFiatLabel = `${labelSign}${formatNumber(
    Math.abs(totalChangeInFiat / 100.0),
    exchange.FIAT_VALUE
  )} ${userFiatCurrencyCode} ${translate('24h_label')}`;

  const { restrictions } = useAccountRestriction();

  const addFundsIsDisabledForAllCorrencies = useMemo(() => {
    let isDisabled = true;
    if (restrictions && wallets) {
      // eslint-disable-next-line array-callback-return
      wallets.map((wallet) => {
        if (!restrictions[wallet?.coin]?.receiveDisabled) {
          isDisabled = false;
        }
      });
    }
    return isDisabled;
  }, [restrictions, wallets]);

  const withdrawIsDisabledForAllCorrencies = useMemo(() => {
    let isDisabled = true;
    if (restrictions && wallets) {
      // eslint-disable-next-line array-callback-return
      wallets.map((wallet) => {
        if (!restrictions[wallet?.coin]?.sendDisabled) {
          isDisabled = false;
        }
      });
    }
    return isDisabled;
  }, [restrictions, wallets]);

  if (loading) {
    return (
      <BasicContainer className="balance-card mb-3">
        <BalanceCardPlaceholder />
      </BasicContainer>
    );
  }

  const renderInfoPopup = () => (
    <div
      className="position-absolute popover-info px-4 py-3 rounded-3"
      style={{ width: '14.375rem', right: '-15.5rem', top: '-110%' }}>
      <p className="text-f6 text-white">{translate('daily_total_change_info')}</p>
      <div
        className="arrow-left-popover position-absolute"
        style={{ top: '20%', left: '-0.55rem' }}
      />
    </div>
  );

  return (
    <BasicContainer className="balance-card mb-3">
      <div className="flex-row d-flex align-items-center justify-content-between">
        <div className="d-flex flex-row align-items-center">
          <p className="text-f6 text-shadow text-bold">{translate('total_balance_label')}</p>
        </div>
        <PercentageBadge percentage={changesPercentage} />
      </div>

      <div className="justify-content-center mt-3">
        <div className="flex-column justify-content-between">
          <p className="h2">{totalBalanceFiatLabel}</p>
          <p
            className={`text-f6 text-shadow d-flex mt-2 ${
              totalChangeInFiat < 0 ? 'text-danger' : 'text-success'
            }`}>
            {totalChangeFiatLabel}{' '}
            <i
              style={{
                width: '1rem',
                height: '1rem',
                lineHeight: '1rem',
                fontSize: '1rem',
              }}
              className={`position-relative icon-question-other  ms-2 cursor-pointer ${
                showPopover ? 'text-dark' : 'text-shadow'
              }`}
              onMouseEnter={() => setShowPopover(true)}
              onMouseLeave={() => setShowPopover(false)}
              onClick={isMobile ? () => showModal(MODAL_SCREENS.TOTAL_CHANGE_INFO) : () => {}}>
              {showPopover && renderInfoPopup()}
            </i>
          </p>

          {onCtaClick && (
            <div className="d-flex flex-row justify-content-center pb-2 pt-4">
              {!isDepositHidden && (
                <button
                  type="button"
                  disabled={addFundsIsDisabledForAllCorrencies}
                  className="btn btn-primary text-f6 w-100 flex-wrap justify-content-center align-content-center d-flex"
                  style={{ maxHeight: '40px' }}
                  onClick={() => {
                    onCtaClick(actionTypes.ADD_FUNDS);
                  }}>
                  {translate('add_funds_btn_title')}
                </button>
              )}
              <button
                type="button"
                className={`btn text-f6 border-grey border text-white ${
                  !isDepositHidden ? 'ms-3' : ''
                } w-100 flex-wrap justify-content-center align-content-center d-flex `}
                style={{ maxHeight: '40px' }}
                disabled={withdrawIsDisabledForAllCorrencies}
                onClick={() => {
                  onCtaClick(actionTypes.WITHDRAW);
                }}>
                {translate('withdraw_button_label')}
              </button>
            </div>
          )}
        </div>
      </div>
    </BasicContainer>
  );
};

Balance.propTypes = {
  totalBalanceInFiat: PropTypes.string.isRequired,
  showModal: PropTypes.func.isRequired,
  fiatCurrencyCode: PropTypes.string,
  isDepositHidden: PropTypes.bool.isRequired,
  onCtaClick: PropTypes.func,
  totalChangeInFiat: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  wallets: PropTypes.array,
  loading: PropTypes.bool,
};
